<template>
  <v-card class="point-explorer-table-card">
    <v-card-title class="pb-2">
      <v-icon left>mdi-table</v-icon>
      Results
      <v-spacer></v-spacer>
    </v-card-title>
    
    <v-card-text class="pa-0">
      <v-data-table
        :headers="headers"
        :items="pointsWithSortValues"
        :items-per-page="itemsPerPage"
        :page="page"
        :loading="loading"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        @update:sort-by="handleSortByChange"
        @update:sort-desc="handleSortDescChange"
        @update:page="handlePageChange"
        @update:items-per-page="handleItemsPerPageChange"
        @click:row="handleRowClick"
        class="point-explorer-results-table haq-data-table"
        hover
        item-key="objectId"
        :disable-pagination="itemsPerPage === -1"
        :hide-default-footer="itemsPerPage === -1"
        :footer-props="footerProps"
      >

        
        <!-- Alert Status column template -->
        <template slot="item.alarmStatus" slot-scope="{ item }">
          <v-chip
            :color="getAlertStatusColor(item.alarmStatus)"
            :text-color="getAlertStatusTextColor(item.alarmStatus)"
            :outlined="!item.alarmStatus"
            small
            class="alert-status-chip"
          >
            {{ getAlertStatusText(item.alarmStatus) }}
          </v-chip>
        </template>
        
        <!-- Site column template -->
        <template slot="item.siteName" slot-scope="{ item }">
          <a 
            @click.stop="handleSiteClick(item.site)" 
            class="site-link"
            v-if="item.site"
          >
            {{ item.site.name }}
          </a>
          <span v-else class="no-data">--</span>
        </template>
        
        <!-- Zone column template -->
        <template slot="item.zoneName" slot-scope="{ item }">
          <a 
            @click.stop="handleZoneClick(item.zone)" 
            class="zone-link"
            v-if="item.zone"
          >
            {{ item.zone.name }}
          </a>
          <span v-else class="no-data">--</span>
        </template>
        
        <!-- Current Value column template -->
        <template slot="item.curVal" slot-scope="{ item }">
          <span v-if="item.curVal !== null && item.curVal !== undefined">
            {{ formatValue(item.curVal) }}
          </span>
          <span v-else class="no-data">--</span>
        </template>
        
        <!-- Unit column template -->
        <template slot="item.unit" slot-scope="{ item }">
          <span v-if="item.unit">{{ item.unit }}</span>
          <span v-else class="no-data">--</span>
        </template>
        
        <!-- Type column template -->
        <template slot="item.type" slot-scope="{ item }">
          <span class="type-cell-inline">
            <span class="type-label">{{ item.type }}</span>
            <MeasurementIcons :measurement="item.type ? item.type.toLowerCase() : ''" :size="16" />
          </span>
        </template>
        
        <!-- View Trend column template -->
        <template slot="item.actions" slot-scope="{ item }">
          <v-btn
            class="haq-btn haq-btn-primary"
            text
            @click.stop="handleViewTrend(item)"
            :loading="loadingTrend === item.objectId"
          >
            <v-icon left small>mdi-chart-line</v-icon>
            VIEW TREND
          </v-btn>
        </template>
        
        <!-- Loading slot -->
        <template slot="loading">
          <v-skeleton-loader
            v-for="n in 5"
            :key="n"
            type="table-row"
            class="mx-2"
          />
        </template>
        
        <!-- No data slot -->
        <template slot="no-data">
          <div class="no-data-container">
            <v-icon size="48" color="grey">mdi-database-search</v-icon>
            <h3 class="mt-4">No Points Found</h3>
            <p class="text-body-2">
              Try adjusting your filters or clearing them to see more results.
            </p>
          </div>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import MeasurementIcons from '@/components/MeasurementIcons.vue';

export default {
  name: 'PointExplorerTable',
  components: {
    MeasurementIcons
  },
  props: {
    points: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    pagination: {
      type: Object,
      default: () => ({
        page: 1,
        limit: 100,
        totalCount: 0,
        totalPages: 0
      })
    }
  },
  data() {
    return {
      // Table configuration
      headers: [
        {
          text: 'Name',
          value: 'name',
          sortable: true,
          width: '144px'
        },
        {
          text: 'Type',
          value: 'type',
          sortable: true,
          width: '150px'
        },
        {
          text: 'Current Value',
          value: 'curVal',
          sortable: true,
          width: '120px',
          align: 'center'
        },
        {
          text: 'Units',
          value: 'unit',
          sortable: true,
          width: '80px'
        },
        {
          text: 'Alert Status',
          value: 'alarmStatus',
          sortable: true,
          width: '120px'
        },
        {
          text: 'Site',
          value: 'siteName',
          sortable: true,
          width: '150px'
        },
        {
          text: 'Zone',
          value: 'zoneName',
          sortable: true,
          width: '150px'
        },
        {
          text: 'View Trend',
          value: 'actions',
          sortable: false,
          width: '120px',
          align: 'center'
        }
      ],
      
      // Current state
      page: 1,
      itemsPerPage: 100,
      sortBy: 'name',
      sortDesc: false,
      loadingTrend: null
    };
  },
  computed: {
    totalCount() {
      return this.points.length || 0;
    },
    
    // Add computed properties for sorting
    pointsWithSortValues() {
      return this.points.map(point => ({
        ...point,
        siteName: point.site?.name || '',
        zoneName: point.zone?.name || ''
      }));
    },

    footerProps() {
      const options = [10, 50, 100];
      if (this.pagination.totalCount > 100) {
        options.push({ text: `All (${this.pagination.totalCount})`, value: -1 });
      }
      return {
        'items-per-page-options': options
      };
    }
  },
  methods: {
    handleRowClick(item) {
      // Emit row click event to parent
      this.$emit('point-click', item);
    },
    
    handleSiteClick(site) {
      // Emit site click event to parent
      this.$emit('site-click', site);
    },
    
    handleZoneClick(zone) {
      // Emit zone click event to parent
      this.$emit('zone-click', zone);
    },
    
    handleViewTrend(item) {
      // Set loading state for this specific item
      this.loadingTrend = item.objectId;
      
      // Emit point click event to parent (same as row click)
      this.$emit('point-click', item);
      
      // Clear loading state after a short delay
      setTimeout(() => {
        this.loadingTrend = null;
      }, 1000);
    },
    
    handlePageChange(page) {
      this.page = page;
      // Emit page change to parent for any additional handling needed
      this.$emit('table-change', {
        page: page,
        itemsPerPage: this.itemsPerPage === -1 ? 'ALL' : this.itemsPerPage,
        sortBy: this.sortBy,
        sortDesc: this.sortDesc
      });
    },
    
    handleItemsPerPageChange(itemsPerPage) {
      this.itemsPerPage = itemsPerPage;
      this.page = 1; // Reset to first page when changing items per page
      // Emit items per page change to parent
      this.$emit('table-change', {
        page: 1,
        itemsPerPage: itemsPerPage === -1 ? 'ALL' : itemsPerPage,
        sortBy: this.sortBy,
        sortDesc: this.sortDesc
      });
    },
    
    handleSortByChange(sortBy) {
      this.sortBy = sortBy;
      this.emitTableChange();
    },
    
    handleSortDescChange(sortDesc) {
      this.sortDesc = sortDesc;
      this.emitTableChange();
    },
    
    emitTableChange() {
      // Only emit table configuration changes for sorting
      this.$emit('table-change', {
        page: this.page,
        itemsPerPage: this.itemsPerPage === -1 ? 'ALL' : this.itemsPerPage,
        sortBy: this.sortBy,
        sortDesc: this.sortDesc
      });
    },
    
    getAlertStatusColor(alarmStatus) {
      // Handle undefined/null alarmStatus
      if (alarmStatus === undefined || alarmStatus === null) {
        return 'rgba(0, 0, 0, 0.3)'; // Very light gray for unknown
      }
      
      if (alarmStatus) {
        return '#fa5f5f'; // Red for active alarms (using direct color value for better compatibility)
      } else {
        return 'rgba(0, 0, 0, 0.1)'; // Light gray for inactive
      }
    },
    
    getAlertStatusTextColor(alarmStatus) {
      return 'white';
      // Handle undefined/null alarmStatus
      // if (alarmStatus === undefined || alarmStatus === null) {
      //   return 'rgba(0, 0, 0, 0.6)'; // Dark gray text for unknown
      // }
      
      // if (alarmStatus) {
      //   return 'white'; // White text on red background
      // } else {
      //   return 'rgba(0, 0, 0, 0.7)'; // Dark gray text on light background
      // }
    },

    getAlertStatusText(alarmStatus) {
      if (alarmStatus === undefined || alarmStatus === null) {
        return 'Unknown';
      }
      return alarmStatus ? 'Active' : 'Not Active';
    },
    
    formatValue(value) {
      if (value === null || value === undefined) {
        return '--';
      }
      
      if (typeof value === 'number') {
        // Round to 2 decimal places if it's a decimal
        return value % 1 === 0 ? value.toString() : value.toFixed(2);
      }
      
      return value.toString();
    }
  },
  watch: {
    pagination: {
      handler(newPagination) {
        // Update local pagination state when prop changes
        this.page = newPagination.page || 1;
        this.itemsPerPage = newPagination.limit || 100;
      },
      deep: true,
      immediate: true
    },
    
    points: {
      handler(newPoints) {
        // Validate that all points have alarmStatus
        const invalidPoints = newPoints.filter(point => typeof point.alarmStatus !== 'boolean' && point.alarmStatus !== undefined && point.alarmStatus !== null);
        if (invalidPoints.length > 0) {
          console.warn('Some points have invalid alarmStatus:', invalidPoints);
        }
        
        // Debug logging in development
        if (process.env.NODE_ENV === 'development' && newPoints.length > 0) {
          const activeCount = newPoints.filter(p => p.alarmStatus === true).length;
          const inactiveCount = newPoints.filter(p => p.alarmStatus === false).length;
          const unknownCount = newPoints.filter(p => p.alarmStatus !== true && p.alarmStatus !== false).length;
          console.log(`PointExplorerTable: ${activeCount} active, ${inactiveCount} inactive, ${unknownCount} unknown alarms out of ${newPoints.length} total points`);
        }
      },
      immediate: true
    }
  },
  
  mounted() {
    if (process.env.NODE_ENV === 'development') {
      console.log('PointExplorerTable mounted with', this.points.length, 'points');
    }
  }
};
</script>

<style lang="sass" scoped>
.point-explorer-table-card
  border-radius: 12px
  box-shadow: var(--box-shadow)
  background: var(--app-secondary-bg) !important
  
  .v-card-title
    background: var(--app-secondary-bg)
    color: var(--app-primary-txt)
    border-bottom: 1px solid var(--border)
    font-weight: 500
    padding: 16px 24px
    
  .v-card-text
    padding: 0

.point-explorer-results-table
  background: var(--app-secondary-bg)
  
  // Table header styling
  ::v-deep .v-data-table-header
    background: var(--app-secondary-bg)
    
    th
      background: var(--app-secondary-bg) !important
      color: var(--app-primary-txt) !important
      font-weight: 500
      border-bottom: 2px solid var(--border)
      
      &:hover
        background: rgba(125, 133, 197, 0.1) !important
        
      .v-data-table-header__icon
        color: var(--app-primary-txt) !important
  
  // Table body styling
  ::v-deep .v-data-table__wrapper
    tbody
      tr
        cursor: pointer
        transition: background-color 0.2s ease
        
        &:nth-child(even)
          background: var(--app-tertiary-bg)
          
        &:hover
          background: rgba(125, 133, 197, 0.1) !important
          
        td
          color: var(--app-primary-txt)
          padding: 12px 16px
          border-bottom: 1px solid var(--border)
          
          &:first-child
            padding-left: 24px
            
          &:last-child
            padding-right: 24px

// Link styling
.site-link, .zone-link
  color: var(--score-blue-color)
  text-decoration: none
  font-weight: 500
  cursor: pointer
  transition: color 0.2s ease
  
  &:hover
    color: var(--score-blue-color)
    text-decoration: underline

// Alert status chip styling
.alert-status-chip
  font-weight: 500
  border-radius: 12px
  min-width: 90px
  justify-content: center
  transition: all 0.2s ease
  
  &.v-chip--outlined
    border-width: 1px
    
  .v-icon
    margin-right: 4px !important
    margin-left: 0 !important
    
  // Active alarm styling
  &:not(.v-chip--outlined)
    box-shadow: 0 2px 4px rgba(250, 95, 95, 0.3)
    
  // Inactive alarm styling  
  &.v-chip--outlined
    border-color: rgba(0, 0, 0, 0.2)
    
    &:hover
      border-color: rgba(0, 0, 0, 0.4)
      background-color: rgba(0, 0, 0, 0.05)
  
// No data styling
.no-data
  color: var(--app-tertiary-txt)
  font-style: italic
  
.no-data-container
  text-align: center
  padding: 60px 20px
  color: var(--app-tertiary-txt)
  
  h3
    color: var(--app-primary-txt)
    margin-bottom: 8px
    
  p
    color: var(--app-tertiary-txt)
    max-width: 400px
    margin: 0 auto

// Ensure icon and type text are inline and vertically centered in the type column
.type-cell-inline
  display: inline-flex
  align-items: center
  gap: 6px

.type-label
  margin-left: 2px

// Button styling
::v-deep .haq-btn
  border-radius: 12px !important
  padding: 8px 24px !important
  font-weight: 500 !important
  text-transform: none !important
  min-width: 100px !important
  height: 2.8em !important
  line-height: 2.8em !important
  
  &.haq-btn-primary
    background: var(--score-blue-color) !important
    color: white !important
    
    &:hover
      background: var(--score-blue-color) !important
      opacity: 0.9 !important
      
    &:disabled
      background: var(--app-tertiary-bg) !important
      color: var(--app-tertiary-txt) !important
      opacity: 0.6 !important

// Responsive design
@media (max-width: 768px)
  .point-explorer-table-card
    .v-card-title
      flex-direction: column
      align-items: flex-start
      gap: 16px
      
      .point-explorer-results-table
      ::v-deep .v-data-table__wrapper
      tbody
        tr
          td
            padding: 8px 12px
            
            &:first-child
              padding-left: 16px
              
            &:last-child
              padding-right: 16px
              
    // Hide less important columns on mobile
    ::v-deep .v-data-table-header
      th:nth-child(4), // Units
      th:nth-child(5)  // Alert Status
        display: none
        
    ::v-deep .v-data-table__wrapper
      tbody
        tr
          td:nth-child(4), // Units
          td:nth-child(5)  // Alert Status
            display: none
          
          // Right-align the actions column (VIEW TREND button) and remove padding
          td:last-child
            text-align: right !important
            padding-right: 8px !important
  
  // Reduce button padding in mobile view and remove margin
  ::v-deep .haq-btn
    padding: 6px 12px !important
    min-width: 80px !important
    margin-right: 0 !important
</style> 